import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { PublicUser } from '@repo/types';
import { getMe, signInWithGoogle, signOut as apiSignOut } from './api';
import { getGuestID } from './guest-id';

interface AuthContextValue {
  /** Signed-in user, or null while a guest (or before the first load resolves). */
  user: PublicUser | null;
  /** True until the initial /api/auth/me check resolves. */
  loading: boolean;
  /** True once loaded with no signed-in user. */
  isGuest: boolean;
  /** True when the signed-in user is the site owner. UX-only — the API re-checks. */
  isAdmin: boolean;
  /** Stable anonymous id for guests (a local marker, never sent as auth). */
  guestID: string;
  signIn: (credential: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);
  const guestID = useMemo(getGuestID, []);

  // Resolve the session once on mount: signed-in user or guest (null).
  useEffect(() => {
    let active = true;
    getMe()
      .then((u) => {
        if (active) setUser(u);
      })
      .catch(() => {
        if (active) setUser(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const signIn = useCallback(async (credential: string) => {
    setUser(await signInWithGoogle(credential));
  }, []);

  const signOut = useCallback(async () => {
    await apiSignOut();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isGuest: !loading && user === null,
      isAdmin: user?.isAdmin ?? false,
      guestID,
      signIn,
      signOut,
    }),
    [user, loading, guestID, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Access auth state and actions. Throws if used outside <AuthProvider>. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
